import React, { useEffect, useState } from "react";
import { X, Upload, ImagePlus, Quote } from "lucide-react";
import { toast } from "sonner";
import { gallery } from "../mock";
import useReveal from "../hooks/useReveal";
import { getApprovedPhotos, uploadPhoto } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

export default function Gallery() {
  const [ref, visible] = useReveal();
  const [active, setActive] = useState(null);
  const [uploaded, setUploaded] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploaderName, setUploaderName] = useState("");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadPhotos = async () => {
    try {
      const data = await getApprovedPhotos();
      setUploaded(data);
    } catch {
      setUploaded([]);
    }
  };

  useEffect(() => {
    loadPhotos();
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Please choose an image under 10MB.");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!uploaderName.trim() || !file) {
      toast.error("Please add your name and choose a photo.");
      return;
    }
    setBusy(true);
    try {
      await uploadPhoto(uploaderName.trim(), file, caption.trim());
      toast.success("Thank you! Your photo will appear once approved.");
      setOpen(false);
      setUploaderName("");
      setCaption("");
      setFile(null);
      setPreview(null);
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const mockItems = gallery.images.map((src) => ({ url: src, caption: "", uploader_name: "" }));
  const allImages = [...mockItems, ...uploaded];

  return (
    <section id="gallery" className="py-24 md:py-32 px-6 bg-[#ECE3D4]">
      <div ref={ref} className={`max-w-[1200px] mx-auto reveal ${visible ? "is-visible" : ""}`}>
        <div className="text-center mb-12">
          <h2 className="font-serif-display text-[#9E2B25] text-[46px] md:text-[68px]">{gallery.title}</h2>
          <p className="mt-4 text-[#4a443d] max-w-xl mx-auto">{gallery.subtitle}</p>
          <button
            onClick={() => setOpen(true)}
            className="mt-8 inline-flex items-center gap-2 bg-[#211E1A] text-[#F4EFE6] text-[12px] uppercase tracking-widest-2 px-7 py-[13px] rounded-sm hover:bg-[#9E2B25] transition-colors duration-300"
          >
            <ImagePlus size={16} /> Share a Photo
          </button>
        </div>

        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance]">
          {allImages.map((item, i) => (
            <button
              key={i}
              onClick={() => setActive(item)}
              className="img-zoom group relative mb-4 block w-full overflow-hidden rounded-sm border-[5px] border-[#F4EFE6] shadow-md focus:outline-none"
            >
              <img
                src={item.url}
                alt={item.caption || `Memory ${i + 1}`}
                className="w-full object-cover"
                style={{ aspectRatio: i % 3 === 0 ? "3/4" : "1/1" }}
              />
              {item.caption ? (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#211E1A]/80 to-transparent p-4 pt-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-left">
                  <p className="text-[#F4EFE6] text-sm leading-snug line-clamp-2">{item.caption}</p>
                  {item.uploader_name ? (
                    <p className="mt-1 text-[#EAD9C9] text-[11px] uppercase tracking-widest-2">— {item.uploader_name}</p>
                  ) : null}
                </div>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-[60] bg-[#211E1A]/90 backdrop-blur-sm flex flex-col items-center justify-center p-4"
          onClick={() => setActive(null)}
        >
          <button
            className="absolute top-6 right-6 text-[#F4EFE6] hover:text-[#9E2B25] transition-colors"
            onClick={() => setActive(null)}
            aria-label="Close"
          >
            <X size={32} />
          </button>
          <img
            src={active.url}
            alt={active.caption || "Enlarged memory"}
            className="max-h-[80vh] max-w-[92vw] object-contain border-[6px] border-[#F4EFE6] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          {active.caption ? (
            <div className="mt-5 max-w-xl text-center" onClick={(e) => e.stopPropagation()}>
              <p className="font-serif-display text-[#F4EFE6] text-xl md:text-2xl leading-snug">“{active.caption}”</p>
              {active.uploader_name ? (
                <p className="mt-2 text-[#EAD9C9] text-[11px] uppercase tracking-widest-2">— {active.uploader_name}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#F4EFE6] border-[#211E1A]/10">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-3xl text-[#211E1A] font-medium">
              Share a Photo
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitUpload} className="space-y-5 mt-2">
            <input
              value={uploaderName}
              onChange={(e) => setUploaderName(e.target.value)}
              placeholder="Your name"
              className="w-full bg-transparent border-b border-[#211E1A]/25 py-2.5 text-[#211E1A] placeholder:text-[#9c9488] focus:outline-none focus:border-[#9E2B25]"
            />
            <div className="relative">
              <Quote size={15} className="absolute left-0 top-3 text-[#9E2B25]" />
              <input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={280}
                placeholder="Add a short note or memory (optional)"
                className="w-full bg-transparent border-b border-[#211E1A]/25 py-2.5 pl-6 text-[#211E1A] placeholder:text-[#9c9488] focus:outline-none focus:border-[#9E2B25]"
              />
            </div>
            <label className="block cursor-pointer">
              <div className="border border-dashed border-[#211E1A]/30 rounded-sm p-6 text-center hover:border-[#9E2B25] transition-colors">
                {preview ? (
                  <img src={preview} alt="Preview" className="max-h-48 mx-auto object-contain rounded-sm" />
                ) : (
                  <div className="text-[#6b6157]">
                    <Upload size={22} className="mx-auto" />
                    <p className="mt-2 text-sm">Click to choose a photo (max 10MB)</p>
                  </div>
                )}
              </div>
              <input type="file" accept="image/*" onChange={onFile} className="hidden" />
            </label>
            <p className="text-[12px] text-[#6b6157]">Photos are reviewed by the couple before appearing here.</p>
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-[#9E2B25] text-[#F4EFE6] text-[12px] uppercase tracking-widest-2 py-3.5 rounded-sm hover:bg-[#872019] transition-colors disabled:opacity-60"
            >
              {busy ? "Uploading..." : "Submit Photo"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
